
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Building2, BarChart, AlertCircle, CheckCircle2, FileText, Calculator, TrendingUp, CreditCard, ArrowRight, ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { saveSeoMetadata } from "@/utils/seoUtils";
import SeoMetadata from "@/components/common/SeoMetadata";

// SEO Keywords for Business Loans
const seoKeywords = [
  "yrityslaina", "yritysrahoitus", "yrityslainan kilpailutus", 
  "pk-yrityksen rahoitus", "investointirahoitus", "käyttöpääomalaina", 
  "vakuudeton yrityslaina", "yrityksen pankkilaina", "kasvurahoitus", 
  "yrityslainan korko", "yritysluotto", "liiketoiminnan rahoitus"
];

const YrityslainaPage = () => {
  const isMobile = useIsMobile();
  
  const seoTitle = "Yrityslaina | Kilpailuta yrityslainat helposti - Lainasampo.fi";
  const seoDescription = "Kilpailuta yrityslainat nopeasti ja helposti Lainasampo.fi:n kautta. Vertaile eri rahoittajien yrityslainoja ja löydä yrityksellesi sopivin rahoitusratkaisu.";
  const pageUrl = "https://lainasampo.fi/lainat/yrityslaina";
  
  // Save SEO metadata to Supabase
  useEffect(() => {
    saveSeoMetadata("/lainat/yrityslaina", {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
    });
  }, []);
  
  // Fade in animation variant
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
      }
    })
  };
  
  return (
    <div className="min-h-screen bg-white p-4 md:p-8">
      <SeoMetadata
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={pageUrl}
      />
      <div className="subpage-container">
        <motion.div
          initial="hidden"
          animate="visible"
          custom={0}
          variants={fadeIn}
          className="mb-6"
        >
          <h1 className="text-3xl font-bold mb-2 text-blue-700">Yrityslaina</h1>
          <p className="text-gray-600 max-w-3xl">
            Kilpailuta yrityslainat helposti ja nopeasti. Vertaile eri rahoittajien tarjouksia ja 
            löydä yrityksellesi sopivin rahoitusratkaisu yhdellä hakemuksella.
          </p>
        </motion.div>
        
        <motion.div 
          className="mb-8"
          initial="hidden"
          animate="visible" 
          custom={1}
          variants={fadeIn}
        >
          <Card className="border border-blue-100 bg-gradient-to-r from-white to-blue-50">
            <CardHeader className="pb-2">
              <div className="flex items-center">
                <Building2 className="h-6 w-6 mr-3 text-blue-600" />
                <CardTitle className="text-2xl text-blue-700">Rahoitusta yritystoimintaan</CardTitle>
              </div>
              <CardDescription className="text-gray-600 text-base">
                Löydä yrityksellesi sopivat rahoitusratkaisut
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Yrityslainat ovat suunniteltu yritysten eri tarpeisiin, olipa kyse sitten investoinneista, 
                  käyttöpääoman tarpeesta tai yrityksen kasvattamisesta.
                </p>
                <p className="text-gray-700">
                  Lainasampo.fi:n kautta voit vertailla eri rahoituslaitosten yrityslainoja ja 
                  löytää yrityksellesi sopivimman rahoitusratkaisun.
                </p>
                
                <div className="flex flex-wrap gap-3 pt-2">
                  <span className="inline-flex items-center bg-blue-100 text-blue-700 text-sm font-medium px-3 py-1 rounded-full">
                    <TrendingUp className="h-4 w-4 mr-1" />
                    500€–10M€
                  </span>
                  <span className="inline-flex items-center bg-blue-100 text-blue-700 text-sm font-medium px-3 py-1 rounded-full">
                    <Calculator className="h-4 w-4 mr-1" />
                    1kk–120kk
                  </span>
                  <span className="inline-flex items-center bg-green-100 text-green-700 text-sm font-medium px-3 py-1 rounded-full">
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Nopea hakemus
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div
          initial="hidden"
          animate="visible"
          custom={2}
          variants={fadeIn}
          className="mb-12"
        >
          <h2 className="text-2xl font-semibold mb-6 text-blue-700">Yrityslainan tarjoajat</h2>
          <p className="mb-6 text-gray-700">
            Yrityksesi kasvun ja kehityksen tueksi olemme koonneet kattavan listan yrityslainapalveluista, 
            jotka tarjoavat rahoitusratkaisuja erilaisiin tarpeisiin. Alla esittelemme näitä palveluita, 
            niiden tarjoamat lainasummat, laina-ajat sekä esimerkit kustannuksista.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Standardized Loan Provider Cards */}
            
            {/* Sortter Card */}
            <Card className="hover:shadow-md transition-shadow border border-gray-200 h-full flex flex-col">
              <div className="relative">
                <div className="absolute top-0 left-0 w-full h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-lg">
                  <div className="flex justify-end px-3 pt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white text-blue-800">
                      Suositeltu
                    </span>
                  </div>
                </div>
                <div className="pt-14 px-6 flex justify-center h-24">
                  <img 
                    src="/lovable-uploads/89f895f5-bf45-414e-a41a-8d60cb0dfd02.png" 
                    alt="Sortter logo" 
                    className="max-h-16 max-w-full object-contain"
                  />
                </div>
              </div>
              <CardContent className="pt-4 flex-grow flex flex-col">
                <div className="space-y-4 flex-grow">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Lainasumma</p>
                      <p className="font-medium">500 € – 10 000 000 €</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Laina-aika</p>
                      <p className="font-medium">1 kk – 120 kk</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Ominaisuus</p>
                    <p className="font-medium">Vakuudeton laina jopa 500 000 € asti</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Lainaesimerkki</p>
                    <p className="text-sm">10 000 € laina, 12 kk, 7 % korolla → kuukausierä noin 865,27 €</p>
                  </div>
                  
                  <div className="pt-2">
                    <p className="text-sm text-gray-700 mb-4">
                      Sortter kilpailuttaa yrityslainat puolestasi noin 20 pankin ja rahoituslaitoksen kesken. 
                      Yhdellä hakemuksella voit saada useita lainatarjouksia, mikä säästää aikaa ja auttaa löytämään edullisimman vaihtoehdon.
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 mt-auto">
                  <a 
                    href="https://go.adt267.com/t/t?a=1869130148&as=1854031059&t=2&tk=1" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-full sm:w-auto"
                  >
                    <Button className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700">
                      Vertaile tarjouksia Sortterilla <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* CapitalBox Card */}
            <Card className="hover:shadow-md transition-shadow border border-gray-200 h-full flex flex-col">
              <div className="relative">
                <div className="absolute top-0 left-0 w-full h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-lg"></div>
                <div className="pt-14 px-6 flex justify-center h-24">
                  <img 
                    src="/lovable-uploads/7d6d2d92-994f-4a43-8b2c-cbc5b8a03503.png" 
                    alt="CapitalBox logo" 
                    className="max-h-16 max-w-full object-contain"
                  />
                </div>
              </div>
              <CardContent className="pt-4 flex-grow flex flex-col">
                <div className="space-y-4 flex-grow">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Lainasumma</p>
                      <p className="font-medium">5 000 € – 3 000 000 €</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Laina-aika</p>
                      <p className="font-medium">1 kk – 60 kk</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Ominaisuus</p>
                    <p className="font-medium">2,5 % perustamismaksu lainasummasta</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Lainaesimerkki</p>
                    <p className="text-sm">50 000 € laina, 36 kk, 10 % korolla → kuukausierä noin 1 613 €</p>
                  </div>
                  
                  <div className="pt-2">
                    <p className="text-sm text-gray-700 mb-4">
                      CapitalBox tarjoaa räätälöityjä lainaratkaisuja yrityksesi tarpeisiin selkein ehdoin ja ilman piilokuluja. 
                      Nopea päätös ja tilillemaksu jopa samana päivänä, ei piilokuluja.
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 mt-auto">
                  <a 
                    href="https://go.adt267.com/t/t?a=1090867560&as=1854031059&t=2&tk=1" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-full sm:w-auto"
                  >
                    <Button className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700">
                      Tutustu CapitalBoxin yrityslainaan <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
            
            {/* Kasvurahoitus Card */}
            <Card className="hover:shadow-md transition-shadow border border-gray-200 h-full flex flex-col">
              <div className="relative">
                <div className="absolute top-0 left-0 w-full h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-lg"></div>
                <div className="pt-14 px-6 flex justify-center h-24">
                  <img 
                    src="/lovable-uploads/04d92b23-d437-4dd0-be68-b5c6d021481d.png" 
                    alt="Kasvurahoitus.fi logo" 
                    className="max-h-16 max-w-full object-contain"
                  />
                </div>
              </div>
              <CardContent className="pt-4 flex-grow flex flex-col">
                <div className="space-y-4 flex-grow">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Lainasumma</p>
                      <p className="font-medium">2 000 € – 250 000 €</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Laina-aika</p>
                      <p className="font-medium">2 kk – 24 kk</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Ominaisuus</p>
                    <p className="font-medium">Ei reaalivakuuksia</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Lainaesimerkki</p>
                    <p className="text-sm">25 000 € laina, 12 kk, 12 % korolla → kuukausierä noin 2 220 €</p>
                  </div>
                  
                  <div className="pt-2">
                    <p className="text-sm text-gray-700 mb-4">
                      Kasvurahoitus.fi tarjoaa yrityksille lainaa ilman reaalivakuuksia. Räätälöidyn lainatarjouksen voi saada jopa alle tunnissa, 
                      ja rahat voivat olla tilillä saman päivän aikana. Erinomainen vaihtoehto pk-yrityksille.
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 mt-auto">
                  <a 
                    href="https://go.adt256.com/t/t?a=1280180878&as=1854031059&t=2&tk=1" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-full sm:w-auto"
                  >
                    <Button className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700">
                      Hae lainaa ilman vakuuksia <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
            
            {/* Kredita Card */}
            <Card className="hover:shadow-md transition-shadow border border-gray-200 h-full flex flex-col">
              <div className="relative">
                <div className="absolute top-0 left-0 w-full h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-lg"></div>
                <div className="pt-14 px-6 flex justify-center h-24">
                  <img 
                    src="/lovable-uploads/3c0ba3a0-b302-422f-8dfa-15d70864df3f.png" 
                    alt="Kredita logo" 
                    className="max-h-16 max-w-full object-contain"
                  />
                </div>
              </div>
              <CardContent className="pt-4 flex-grow flex flex-col">
                <div className="space-y-4 flex-grow">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Lainasumma</p>
                      <p className="font-medium">1 000 € – 3 000 000 €</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Laina-aika</p>
                      <p className="font-medium">1 kk – 48 kk</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Ominaisuus</p>
                    <p className="font-medium">Kilpailuttaa useita rahoittajia</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Lainaesimerkki</p>
                    <p className="text-sm">100 000 € laina, 36 kk, 8 % korolla → kuukausierä noin 3 134 €</p>
                  </div>
                  
                  <div className="pt-2">
                    <p className="text-sm text-gray-700 mb-4">
                      Kredita kilpailuttaa puolestasi useita lainanantajia, jotta löydät yrityksellesi sopivimman rahoitusratkaisun. 
                      Hakemuksen täyttäminen on helppoa ja maksuton, ja lainatarjouksen voi saada jopa 24 tunnin sisällä.
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 mt-auto">
                  <a 
                    href="https://go.adt253.net/t/t?a=1748120981&as=1854031059&t=2&tk=1" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-full sm:w-auto"
                  >
                    <Button className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700">
                      Kilpailuta lainat Kreditalla <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
            
            {/* Qred Card */}
            <Card className="hover:shadow-md transition-shadow border border-gray-200 h-full flex flex-col md:col-span-2 lg:col-span-1">
              <div className="relative">
                <div className="absolute top-0 left-0 w-full h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-lg"></div>
                <div className="pt-14 px-6 flex justify-center h-24">
                  <img 
                    src="/lovable-uploads/a2efd10c-f298-4a1a-8097-30ad0c4c975c.png" 
                    alt="Qred logo" 
                    className="max-h-16 max-w-full object-contain"
                  />
                </div>
              </div>
              <CardContent className="pt-4 flex-grow flex flex-col">
                <div className="space-y-4 flex-grow">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Lainasumma</p>
                      <p className="font-medium">1 000 € – 200 000 €</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Laina-aika</p>
                      <p className="font-medium">1 kk – 24 kk</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Ominaisuus</p>
                    <p className="font-medium">Kuukausikulu alkaen 1,75 %</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Lainaesimerkki</p>
                    <p className="text-sm">30 000 € laina, 12 kk, 1,9 % kuukausikulu → kuukausierä noin 2 570 €</p>
                  </div>
                  
                  <div className="pt-2">
                    <p className="text-sm text-gray-700 mb-4">
                      Qred tarjoaa yrityslainaa ilman korkoa; ainoa kulu on kuukausittainen maksu, joka alkaa 1,75 %:sta. 
                      Ei avausmaksuja tai piilokuluja, mikä tekee lainasta läpinäkyvän ja ennustettavan.
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 mt-auto">
                  <a 
                    href="https://go.adt231.net/t/t?a=1180852035&as=1854031059&t=2&tk=1" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-full sm:w-auto"
                  >
                    <Button className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700">
                      Tutustu korottomaan Qred-lainaan <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div initial="hidden" animate="visible" custom={3} variants={fadeIn}>
            <Card className="h-full bg-white hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                  <CardTitle className="text-blue-700">Yrityslainan käyttökohteet</CardTitle>
                </div>
                <CardDescription>Mihin yrityslainaa voi käyttää?</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Käyttöpääoman</span> hankkiminen
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Liiketoiminnan laajentaminen</span> ja kehittäminen
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Yrityskaupat ja -järjestelyt</span> sekä yritysostot
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Koneet, kalusto ja laitteet</span> sekä muut investoinnit
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Toimitilojen hankinta tai remontointi</span> yrityksen tarpeisiin
                    </span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-gray-700 text-sm">
                    Yrityslainan käyttötarkoitus vaikuttaa lainan ehtoihin ja rahoituksen saatavuuteen.
                    Selkeä käyttötarkoitus helpottaa lainan myöntämistä.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial="hidden" animate="visible" custom={4} variants={fadeIn}>
            <Card className="h-full bg-white hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center">
                  <BarChart className="h-5 w-5 mr-2 text-blue-600" />
                  <CardTitle className="text-blue-700">Yrityslainan hakeminen</CardTitle>
                </div>
                <CardDescription>Näin saat rahoituksen yrityksellesi</CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3 list-decimal pl-5">
                  <li className="text-gray-700">
                    <span className="font-medium">Valmistele liiketoimintasuunnitelma</span> ja kassavirtalaskelma
                  </li>
                  <li className="text-gray-700">
                    <span className="font-medium">Kokoa yrityksen tilinpäätöstiedot</span> vähintään kahdelta vuodelta
                  </li>
                  <li className="text-gray-700">
                    <span className="font-medium">Täytä yrityslainahakemus</span> Lainasampo.fi:ssä
                  </li>
                  <li className="text-gray-700">
                    <span className="font-medium">Vertaile eri rahoittajien</span> tarjouksia
                  </li>
                  <li className="text-gray-700">
                    <span className="font-medium">Neuvottele ehdoista</span> ja vakuuksista
                  </li>
                </ol>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-gray-700 text-sm">
                    Huolellinen valmistautuminen rahoituksen hakemiseen parantaa onnistumisen 
                    mahdollisuuksia ja voi johtaa parempiin lainaehtoihin.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        <motion.div 
          initial="hidden" 
          animate="visible" 
          custom={5} 
          variants={fadeIn}
          className="mb-8"
        >
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
                <CardTitle className="text-xl text-blue-700">Mitä tulee huomioida yrityslainaa hakiessa?</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium text-blue-700 mb-2">Takaisinmaksukyky</h3>
                <p className="text-gray-700">
                  Rahoituslaitokset arvioivat ensisijaisesti yrityksen kykyä maksaa laina takaisin. 
                  Kassavirtalaskelmat, tulosennusteet ja aiempi taloushistoria ovat avainasemassa 
                  lainapäätöksessä.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium text-blue-700 mb-2">Vakuudet</h3>
                <p className="text-gray-700">
                  Yrityslainat vaativat usein vakuuksia. Näitä voivat olla yrityksen omaisuus, 
                  yrittäjän henkilökohtainen omaisuus tai ulkopuolisen tahon takaus. Vakuuksien 
                  tarpeen määrä riippuu lainasummasta ja yrityksen taloudellisesta tilanteesta.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium text-blue-700 mb-2">Rahoituksen kustannukset</h3>
                <p className="text-gray-700">
                  Huomioi kaikki yrityslainaan liittyvät kustannukset: korko, järjestelypalkkiot, 
                  nostopalkkiot ja muut mahdolliset kulut. Nämä voivat vaihdella merkittävästi eri 
                  rahoittajien välillä, joten kilpailuttaminen kannattaa.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <Calculator className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-700 mb-1">Kokonaistaloudellinen laskelma</p>
                      <p className="text-sm text-gray-700">
                        Tee tarkka laskelma investoinnin tuotto-odotuksista verrattuna lainan kustannuksiin.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <TrendingUp className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-700 mb-1">Kasvun hallinta</p>
                      <p className="text-sm text-gray-700">
                        Liian nopea kasvu voi johtaa kassavirtaongelmiin. Varaudu kasvun aiheuttamiin kustannuksiin.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <CreditCard className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-700 mb-1">Korkovaihtelu</p>
                      <p className="text-sm text-gray-700">
                        Varaudu mahdollisiin korkojen nousuihin yrityksen kassavirtalaskelmissa.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div
          initial="hidden"
          animate="visible"
          custom={6}
          variants={fadeIn}
          className="mb-8"
        >
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                <CardTitle className="text-xl text-blue-700">Hyödyllisiä artikkeleita yritysrahoituksesta</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/artikkelit/yrityslaina-opas" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                  <h3 className="font-medium text-blue-700 mb-2">Yrityslainan hakijan opas</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Kattava opas yritysrahoituksen hakemiseen, tarvittaviin asiakirjoihin ja 
                    onnistuneen rahoitusneuvottelun käymiseen.
                  </p>
                  <p className="text-blue-600 text-sm font-medium">Lue lisää →</p>
                </Link>
                
                <Link to="/artikkelit/yrityksen-kassavirta" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                  <h3 className="font-medium text-blue-700 mb-2">Kassavirran hallinta ja rahoitustarpeet ennakolta</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Miten hallita yrityksen kassavirtaa tehokkaasti ja suunnitella rahoitustarpeet ennakolta.
                  </p>
                  <p className="text-blue-600 text-sm font-medium">Lue lisää →</p>
                </Link>
                
                <Link to="/artikkelit/yrityslainan-vakuudet" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                  <h3 className="font-medium text-blue-700 mb-2">Yrityslainan vakuudet</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Millaisia vakuuksia yrityslainaan tarvitaan ja miten rahoittajat arvioivat vakuuksien arvoa?
                  </p>
                  <p className="text-blue-600 text-sm font-medium">Lue lisää →</p>
                </Link>
                
                <Link to="/artikkelit/bisnesenkeli-vai-pankki" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                  <h3 className="font-medium text-blue-700 mb-2">Yrityslaina vai sijoittaja?</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Vieraan pääoman (lainat) ja oman pääoman (sijoitukset) vertailu: kumpi sopii yrityksellesi paremmin?
                  </p>
                  <p className="text-blue-600 text-sm font-medium">Lue lisää →</p>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div 
          initial="hidden" 
          animate="visible" 
          custom={7} 
          variants={fadeIn}
          className="mb-8"
        >
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-700">Kilpailuta yrityslainasi</h2>
            <p className="text-gray-700 mb-6">
              Säästä yrityksesi rahaa kilpailuttamalla yrityslaina Lainasampo.fi:n kautta. 
              Täytä yksi hakemus ja saat tarjoukset usealta eri rahoituslaitokselta. Vertaile tarjouksia ja 
              valitse yrityksellesi sopivin rahoitusratkaisu.
            </p>
            <Link to="/kilpailuta">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 font-medium">
                Kilpailuta yrityslainasi nyt <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default YrityslainaPage;
